import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Plus, Wallet, Loader2, Calculator, PiggyBank, UserPlus, Mail, Trash2, RefreshCw, AlertTriangle, ClipboardCheck, FileDown, Pencil, FileText,
} from 'lucide-react';
import { CopyButton } from '../PlanManagement/ui';
import { isoDate } from '../../utils/time';
import { Dialog } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, Panel, Register, FigureStrip, Figure, DetailDrawer, AcStatus,
  Currency, MoneyStat, StatRow, Field, FormRow, fmtDate, money,
} from './design';
import Supporters from './Supporters';
import ExitWorkflow from './ExitWorkflow';
import AgreementVersions from './AgreementVersions';
import SelfManagement from './SelfManagement';

const INP = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// Primary supplements. Those with a government rate are paid at the rate in force on the day
// (Schedule of Subsidies and Supplements) unless an own figure is entered; the last two are
// Home Care Package era supplements, entered as a figure.
const SUPPLEMENTS = [
  { key: 'oxygen', label: 'Oxygen', gov: true },
  { key: 'enteral_bolus', label: 'Enteral feeding (bolus)', gov: true },
  { key: 'enteral_non_bolus', label: 'Enteral feeding (non-bolus)', gov: true },
  { key: 'veterans', label: "Veterans'", gov: true },
  { key: 'dementia', label: 'Dementia and cognition (transitioned HCP)', gov: true },
  { key: 'eachd', label: 'EACH-D top-up', gov: true },
  { key: 'aria', label: 'ARIA / Viability (HCP)' },
  { key: 'hardship', label: 'Hardship (HCP)' },
];
const SUPP_LABEL = Object.fromEntries(SUPPLEMENTS.map((s) => [s.key, s.label]));
// A supplement's daily figure for a preview: the provider's own figure, else the government rate
// (a share of the daily subsidy for veterans' and dementia and cognition).
const suppDaily = (key, v, rates, dailySubsidy) => {
  if (v !== 'ref' && v !== '' && v != null) return Number(v) || 0;
  const r = rates && rates[key];
  if (!r) return 0;
  return r.pct ? Math.round(Number(dailySubsidy || 0) * r.value) / 100 : Number(r.value) || 0;
};

const SCHEME_NAME = { SAH: 'Support at Home', HCP: 'Home Care Package' };
const schemeName = (s) => SCHEME_NAME[String(s || '').toUpperCase()] || (s || '—');
const levelLabel = (p) => {
  const level = p.level ?? p.classification;
  return p.scheme === 'HCP' ? `Level ${level ?? '—'}` : `Classification ${level ?? '—'}`;
};

// A funding plan is either published or still a draft; the same two words, the same two colours,
// in the register and in the drawer header.
const PlanStatus = ({ status }) => {
  const published = String(status || '').toLowerCase() === 'published';
  return <AcStatus status={published ? 'active' : 'draft'} label={published ? 'Published' : 'Draft'} />;
};

// Compute the period budget maths from a plan record (works for both API rows and locally-built rows)
function computeBreakdown(plan) {
  const period = (plan.period || (plan.scheme === 'HCP' ? 'Monthly' : 'Quarterly'));
  const days = period === 'Monthly' ? 30 : 91; // SAH quarterly ~91 days, HCP monthly ~30 days
  const dailySubsidy = Number(plan.daily_subsidy_rate ?? plan.dailySubsidyRate ?? 0);
  const subsidy = dailySubsidy * days;

  const supplements = plan.supplements || {};
  // supplements stored either as { key: amount } map or array — normalise to a map of daily $
  let supplementsDaily = 0;
  const supDetail = [];
  if (Array.isArray(plan.supplement_lines)) {
    // Worked out by the server at the rates in force today.
    plan.supplement_lines.forEach((l) => {
      const amt = Number(l.daily || 0);
      if (amt > 0) { supplementsDaily += amt; supDetail.push({ label: `${SUPP_LABEL[l.key] || l.key}${l.from === 'government' ? ' (government rate)' : ''}`, amount: amt * days }); }
    });
  } else {
    SUPPLEMENTS.forEach(({ key, label }) => {
      const amt = Number(supplements[key] || 0);
      if (amt > 0) { supplementsDaily += amt; supDetail.push({ label, amount: amt * days }); }
    });
  }
  const supplementsTotal = supplementsDaily * days;

  const grossDaily = dailySubsidy + supplementsDaily;
  const grossPeriod = subsidy + supplementsTotal;

  // Support at Home: 10% of the budget goes to the provider's pooled care management account and
  // there is no package management or income-tested fee. The percentage fields only apply to a
  // legacy Home Care Package plan.
  const sah = String(plan.scheme || '').toUpperCase() !== 'HCP';
  const careMgmt = sah ? grossPeriod * 0.10 : (plan.care_mgmt_is_pct ?? plan.careMgmtIsPct
    ? grossPeriod * (Number(plan.care_mgmt_value ?? plan.careMgmtValue ?? 0) / 100)
    : Number(plan.care_mgmt_value ?? plan.careMgmtValue ?? 0));
  const pkgMgmt = sah ? 0 : (plan.pkg_mgmt_is_pct ?? plan.pkgMgmtIsPct
    ? grossPeriod * (Number(plan.pkg_mgmt_value ?? plan.pkgMgmtValue ?? 0) / 100)
    : Number(plan.pkg_mgmt_value ?? plan.pkgMgmtValue ?? 0));

  const incomeTested = sah ? 0 : Number(plan.income_tested_fee ?? plan.incomeTestedFee ?? 0);

  // Worked out from its parts whenever there is a rate, so a corrected rate, a supplement added
  // later or a reclassification shows at once. The figure saved when the plan was made is only
  // used for a plan with no rate at all.
  const computed = grossPeriod - careMgmt - pkgMgmt - incomeTested;
  const totalBudget = Number(dailySubsidy > 0 || supplementsDaily > 0 || plan.budget == null ? computed : plan.budget);

  const spent = Number(plan.spent ?? 0);
  const allocated = Number(plan.allocated ?? spent);
  const balance = totalBudget - spent;

  return {
    sah, period, days, dailySubsidy, subsidy,
    supplementsTotal, supDetail,
    careMgmt, pkgMgmt, incomeTested,
    totalBudget, allocated, utilised: spent, balance,
  };
}

// The one place a centred modal is still right: a create form that has nothing to do with an
// existing row. Built on the shared Dialog, so Escape closes it, focus stays inside it while it is
// open and goes back to the button that opened it.
function CreateModal({ title, onClose, footer, size = 'lg', children }) {
  // The Dialog re-runs its focus effect whenever onClose changes; a caller's inline arrow would pull
  // focus out of the form on every keystroke, so hand it a stable one.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const close = useCallback(() => { if (closeRef.current) closeRef.current(); }, []);
  return (
    <Dialog open onClose={close} title={title} footer={footer} size={size}>
      <div className="space-y-4">{children}</div>
    </Dialog>
  );
}

function NewFundingModal({ clients, onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    clientId: '',
    scheme: 'SAH',
    level: '1',
    startDate: isoDate(),
    period: 'Quarterly',
    dailySubsidyRate: '',
    supplements: {},
    careMgmtIsPct: true,
    careMgmtValue: '',
    pkgMgmtIsPct: true,
    pkgMgmtValue: '',
    basicDailyFee: '',
    incomeTestedFee: '',
    establishmentFee: '',
    transferredAmount: '',
    meansCategory: 'full_pensioner',
    isInterim: false,
    noWorseOff: false,
    protectedContributionPct: '',
    independencePct: '',
    everydayPct: '',
    contributionNoticeDate: '',
    hcpUnspentProvider: '',
    hcpUnspentGovernment: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // when scheme switches, reset level + sensible period
  const onScheme = s => setForm(f => ({
    ...f,
    scheme: s,
    level: '1',
    period: s === 'HCP' ? 'Monthly' : 'Quarterly',
  }));

  const toggleSupplement = key => setForm(f => {
    const sup = { ...f.supplements };
    if (key in sup) delete sup[key]; else sup[key] = '';
    return { ...f, supplements: sup };
  });
  const setSupplementAmt = (key, v) => setForm(f => ({ ...f, supplements: { ...f.supplements, [key]: v } }));

  const levels = form.scheme === 'HCP' ? [1, 2, 3, 4] : [1, 2, 3, 4, 5, 6, 7, 8];
  const [suppRates, setSuppRates] = useState(null);
  useEffect(() => { api.get('/aged-care/supplement-rates').then((r) => setSuppRates(r.data.rates)).catch(() => setSuppRates(null)); }, []);
  const isGov = (key) => SUPPLEMENTS.some((x) => x.key === key && x.gov);

  const preview = computeBreakdown({
    scheme: form.scheme,
    period: form.period,
    daily_subsidy_rate: form.dailySubsidyRate,
    supplements: Object.fromEntries(Object.entries(form.supplements).map(([k, v]) => [k, isGov(k) ? suppDaily(k, v, suppRates, form.dailySubsidyRate) : v])),
    care_mgmt_is_pct: form.careMgmtIsPct,
    care_mgmt_value: form.careMgmtValue,
    pkg_mgmt_is_pct: form.pkgMgmtIsPct,
    pkg_mgmt_value: form.pkgMgmtValue,
    income_tested_fee: form.incomeTestedFee,
  });

  const save = async () => {
    if (!form.clientId) return toast.error('Select a client');
    if (!form.dailySubsidyRate) return toast.error('Enter a daily subsidy rate');
    setSaving(true);
    try {
      await api.post('/aged-care/funding', {
        clientId: form.clientId,
        scheme: form.scheme,
        level: Number(form.level),
        startDate: form.startDate,
        period: form.period,
        dailySubsidyRate: Number(form.dailySubsidyRate) || 0,
        supplements: Object.fromEntries(Object.entries(form.supplements).map(([k, v]) => [k, isGov(k) && (v === '' || v == null) ? 'ref' : v])),
        careMgmtIsPct: form.careMgmtIsPct,
        careMgmtValue: Number(form.careMgmtValue) || 0,
        pkgMgmtIsPct: form.pkgMgmtIsPct,
        pkgMgmtValue: Number(form.pkgMgmtValue) || 0,
        basicDailyFee: Number(form.basicDailyFee) || 0,
        incomeTestedFee: Number(form.incomeTestedFee) || 0,
        establishmentFee: Number(form.establishmentFee) || 0,
        transferredAmount: Number(form.transferredAmount) || 0,
        meansCategory: form.meansCategory,
        isInterim: form.isInterim,
        noWorseOff: form.noWorseOff,
        protectedContributionPct: form.noWorseOff ? form.protectedContributionPct : '',
        independencePct: form.independencePct,
        everydayPct: form.everydayPct,
        contributionNoticeDate: form.contributionNoticeDate || null,
        hcpUnspentProvider: Number(form.hcpUnspentProvider) || 0,
        hcpUnspentGovernment: Number(form.hcpUnspentGovernment) || 0,
        budget: preview.totalBudget,
      });
      toast.success('Funding plan created');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not create the funding plan');
    } finally {
      setSaving(false);
    }
  };

  // Percent-or-dollars switch for the two management fees. Plain markup, not a nested component,
  // so it is not remounted on every keystroke.
  const pctToggle = (k) => (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs shrink-0">
      <button type="button" onClick={() => set(k, true)} aria-pressed={!!form[k]} aria-label="Percentage"
        className={clsx('px-3', form[k] ? 'bg-primary-600 text-white' : 'bg-white text-slate-600')}>%</button>
      <button type="button" onClick={() => set(k, false)} aria-pressed={!form[k]} aria-label="Dollar amount"
        className={clsx('px-3', !form[k] ? 'bg-primary-600 text-white' : 'bg-white text-slate-600')}>$</button>
    </div>
  );

  return (
    <CreateModal
      title="New funding plan"
      onClose={onClose}
      footer={<>
        <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          Create plan
        </button>
      </>}
    >
      <Field label="Client" required hint={clients.length === 0 ? 'No aged care clients yet. Enter a client ID, or add the participant first.' : undefined}>
        {(id) => clients.length > 0 ? (
          <select id={id} className={INP} value={form.clientId} onChange={e => set('clientId', e.target.value)}>
            <option value="">Select a client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : (
          <input id={id} className={INP} placeholder="Client ID" value={form.clientId} onChange={e => set('clientId', e.target.value)} />
        )}
      </Field>

      <FormRow cols={3}>
        <Field label="Scheme">
          {(id) => (
            <select id={id} className={INP} value={form.scheme} onChange={e => onScheme(e.target.value)}>
              <option value="SAH">SAH, Support at Home</option>
              <option value="HCP">HCP, Home Care Package</option>
            </select>
          )}
        </Field>
        <Field label="Classification or level">
          {(id) => (
            <select id={id} className={INP} value={form.level} onChange={e => set('level', e.target.value)}>
              {levels.map(l => <option key={l} value={l}>{form.scheme === 'SAH' ? `Classification ${l}` : `Level ${l}`}</option>)}
            </select>
          )}
        </Field>
        <Field label="Budget period">
          {(id) => (
            <select id={id} className={INP} value={form.period} onChange={e => set('period', e.target.value)}>
              <option value="Quarterly">Quarterly</option>
              <option value="Monthly">Monthly</option>
            </select>
          )}
        </Field>
      </FormRow>

      <Field label="Means category" hint="From the Services Australia income and assets assessment. It sets the participant's contribution percentage.">
        {(id) => (
          <select id={id} className={INP} value={form.meansCategory} onChange={e => set('meansCategory', e.target.value)}>
            <option value="full_pensioner">Full pensioner</option>
            <option value="part_pensioner">Part pensioner</option>
            <option value="self_funded">Self-funded</option>
            <option value="not_disclosed">Not assessed or not disclosed (maximum rates)</option>
          </select>
        )}
      </Field>

      <ContributionNoticeFields form={form} set={set} />

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={form.isInterim} onChange={e => set('isInterim', e.target.checked)}
            className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
          Interim funding. Only 60% of the budget is available until the full allocation arrives.
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={form.noWorseOff} onChange={e => set('noWorseOff', e.target.checked)}
            className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
          No worse off. A grandfathered Home Care Package transitionee.
        </label>
        {form.noWorseOff && (
          <div className="pl-6">
            <Field label="Protected contribution %" hint="Their prior HCP effective contribution percentage. The participant is charged the lower of this and the standard means-tested rate, never more." className="max-w-xs">
              {(id) => <input id={id} type="number" min="0" max="100" step="0.01" value={form.protectedContributionPct}
                onChange={e => set('protectedContributionPct', e.target.value)} placeholder="0" className={INP} />}
            </Field>
          </div>
        )}
      </div>

      <FormRow cols={2}>
        <Field label="Start date">
          {(id) => <input id={id} type="date" className={INP} value={form.startDate} onChange={e => set('startDate', e.target.value)} />}
        </Field>
        <Field label="Daily subsidy rate ($)" required>
          {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} placeholder="0.00"
            value={form.dailySubsidyRate} onChange={e => set('dailySubsidyRate', e.target.value)} />}
        </Field>
      </FormRow>

      <Field label="Supplements, daily $" hint="Leave the figure blank to pay the government rate in force on each day.">
        <div className="space-y-2 border border-slate-200 rounded-lg p-3">
          {SUPPLEMENTS.map(({ key, label }) => {
            const on = key in form.supplements;
            return (
              <div key={key} className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer flex-1">
                  <input type="checkbox" checked={on} onChange={() => toggleSupplement(key)}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
                  <span className="text-sm text-slate-600">{label}</span>
                </label>
                {on && (
                  <div className="relative w-32">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                    <input type="number" min="0" step="0.01" aria-label={`${label} daily amount`}
                      className={clsx(INP, '!pl-6 !pr-2 !py-1.5')} placeholder={isGov(key) ? String(suppDaily(key, '', suppRates, form.dailySubsidyRate).toFixed(2)) : '0.00'}
                      value={form.supplements[key]} onChange={e => setSupplementAmt(key, e.target.value)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Field>

      {form.scheme === 'SAH' && (
        <>
          <p className="text-[12px] text-slate-500 -mt-1">
            Support at Home has no package management or income-tested fee. Services Australia puts 10% of every quarterly
            budget into your care management account automatically.
          </p>
          <FormRow cols={2}>
            <Field label="Unspent HCP funds held by you ($)" hint="Only for a participant who moved over from a Home Care Package before 1 September 2021. Used first.">
              {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} placeholder="0.00"
                value={form.hcpUnspentProvider} onChange={e => set('hcpUnspentProvider', e.target.value)} />}
            </Field>
            <Field label="Unspent HCP funds held by Services Australia ($)" hint="Used after the amount you hold.">
              {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} placeholder="0.00"
                value={form.hcpUnspentGovernment} onChange={e => set('hcpUnspentGovernment', e.target.value)} />}
            </Field>
          </FormRow>
        </>
      )}

      {form.scheme === 'HCP' && (<>
      <FormRow cols={2}>
        <Field label="Care management">
          {(id) => (
            <div className="flex gap-2">
              {pctToggle('careMgmtIsPct')}
              <input id={id} type="number" min="0" step="0.01" className={INP}
                placeholder={form.careMgmtIsPct ? '%' : '$ a period'}
                value={form.careMgmtValue} onChange={e => set('careMgmtValue', e.target.value)} />
            </div>
          )}
        </Field>
        <Field label="Package management">
          {(id) => (
            <div className="flex gap-2">
              {pctToggle('pkgMgmtIsPct')}
              <input id={id} type="number" min="0" step="0.01" className={INP}
                placeholder={form.pkgMgmtIsPct ? '%' : '$ a period'}
                value={form.pkgMgmtValue} onChange={e => set('pkgMgmtValue', e.target.value)} />
            </div>
          )}
        </Field>
      </FormRow>

      <FormRow cols={2}>
        <Field label="Basic daily fee ($)">
          {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} placeholder="0.00"
            value={form.basicDailyFee} onChange={e => set('basicDailyFee', e.target.value)} />}
        </Field>
        <Field label="Income-tested care fee ($)">
          {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} placeholder="0.00"
            value={form.incomeTestedFee} onChange={e => set('incomeTestedFee', e.target.value)} />}
        </Field>
      </FormRow>
      <FormRow cols={2}>
        <Field label="Establishment fee ($)">
          {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} placeholder="0.00"
            value={form.establishmentFee} onChange={e => set('establishmentFee', e.target.value)} />}
        </Field>
      </FormRow>
      </>)}

      <FormRow cols={2}>
        <Field label="Transferred from the prior provider ($)">
          {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} placeholder="0.00"
            value={form.transferredAmount} onChange={e => set('transferredAmount', e.target.value)} />}
        </Field>
      </FormRow>

      <Panel tone="muted" icon={Calculator} title={`Estimated ${preview.period.toLowerCase()} budget, ${preview.days} days`} bodyClass="px-4 py-3">
        <div className="space-y-1 text-[13px]">
          <Row label={`Government subsidy at ${money(preview.dailySubsidy)} a day`} value={money(preview.subsidy)} />
          <Row label="Supplements" value={money(preview.supplementsTotal)} />
          <Row label={preview.sah ? 'To the care management account, 10%' : 'Less care management'} value={'− ' + money(preview.careMgmt)} muted />
          {!preview.sah && <Row label="Less package management" value={'− ' + money(preview.pkgMgmt)} muted />}
          {!preview.sah && <Row label="Less income-tested fee" value={'− ' + money(preview.incomeTested)} muted />}
          <div className="flex justify-between pt-2 mt-1 border-t border-slate-200">
            <span className="font-semibold text-slate-800">Total budget</span>
            <span className="font-semibold text-slate-900 tabular-nums">{money(preview.totalBudget)}</span>
          </div>
        </div>
      </Panel>
    </CreateModal>
  );
}

const Row = ({ label, value, muted, strong }) => (
  <div className="flex justify-between gap-4">
    <span className={muted ? 'text-slate-500' : 'text-slate-600'}>{label}</span>
    <span className={clsx('tabular-nums', strong ? 'font-semibold text-slate-800' : 'text-slate-700')}>{value}</span>
  </div>
);

// Support at Home participant/family portal access — provision logins from the funding plan,
// mirroring the PM participant portal. Family/nominee gets read-only budget + statement visibility.
function PortalAccess({ clientId, clientName }) {
  const [accounts, setAccounts] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', relationship: 'Participant' });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);   // shown when the invite email did not go
  const confirm = useConfirm();
  const load = () => api.get('/aged-care-portal/accounts', { params: { client_id: clientId } }).then((r) => setAccounts(r.data || [])).catch(() => setAccounts([]));
  useEffect(() => { if (open && accounts === null) load(); /* eslint-disable-next-line */ }, [open]);
  const invite = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setBusy(true);
    try {
      const r = await api.post('/aged-care-portal/accounts', { client_id: clientId, name: form.name.trim(), email: form.email.trim(), relationship: form.relationship.trim() });
      toast.success(r.data.emailed ? 'Invite emailed' : 'Account created, but the email did not go. Copy the link below and send it to them.');
      setInviteLink(!r.data.emailed && r.data.invite_url ? r.data.invite_url : null);
      setForm({ name: '', email: '', relationship: 'Participant' }); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not create access'); }
    finally { setBusy(false); }
  };
  const resend = async (id) => { try { const r = await api.post(`/aged-care-portal/accounts/${id}/resend`, {}); toast.success(r.data.emailed ? 'Invite re-sent' : 'New link generated, but the email failed'); load(); } catch { toast.error('Could not resend the invite'); } };
  const revoke = async (id) => {
    const ok = await confirm('They can no longer sign in to see the budget and statements.', { title: 'Revoke this portal access?', confirmLabel: 'Revoke access', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care-portal/accounts/${id}`); toast.success('Access revoked'); load(); } catch { toast.error('Could not revoke access'); } };
  // 'invited' is a waiting state; the status vocabulary already has a yellow for that.
  const STATUS_KEY = { active: 'active', invited: 'pending', revoked: 'revoked' };
  const statusLabel = (s) => String(s || '').replace(/^\w/, (c) => c.toUpperCase());
  return (
    <Panel
      title="Participant and family portal access" icon={UserPlus}
      actions={<button type="button" onClick={() => setOpen((o) => !o)} className="btn-ghost btn-sm">{open ? 'Hide' : 'Show'}</button>}
    >
      {open && (
        <div className="px-4 py-3 space-y-3">
          <p className="text-[12px] text-slate-500">Give {clientName || 'the participant'} a private, read-only login to see the live budget, monthly statements and services. Family members and nominees get access from Supporters above, once their consent to share is recorded.</p>
          {inviteLink && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex items-center gap-2">
              <span className="flex-1 min-w-0 break-all">The invite email did not go. Send them this link: {inviteLink}</span>
              <CopyButton value={inviteLink} ariaLabel="Copy the invite link" />
            </div>
          )}
          {accounts === null ? <p className="text-[12px] text-slate-500">Loading accounts…</p> : accounts.length > 0 && (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {accounts.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 truncate">{a.name || a.email}</p>
                    <p className="text-[11px] text-slate-500 truncate">{a.email}{a.relationship ? `, ${a.relationship}` : ''}</p>
                  </div>
                  <AcStatus status={STATUS_KEY[a.status] || a.status} label={statusLabel(a.status)} />
                  <button type="button" onClick={() => resend(a.id)} title="Resend invite" aria-label="Resend invite" className="btn-ghost btn-sm !px-1.5"><RefreshCw size={14} /></button>
                  <button type="button" onClick={() => revoke(a.id)} title="Revoke access" aria-label="Revoke access" className="btn-ghost btn-sm !px-1.5 hover:!text-red-700"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={invite} className="space-y-3">
            <FormRow cols={3}>
              <Field label="Name">{(id) => <input id={id} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={INP} placeholder="Optional" />}</Field>
              <Field label="Email" required>{(id) => <input id={id} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={INP} placeholder="name@example.com" required />}</Field>
              <Field label="For">{() => <p className="text-[13px] text-slate-700 py-2">{clientName || 'The participant'} themselves</p>}</Field>
            </FormRow>
            <div className="flex justify-end">
              <button type="submit" disabled={busy} className="btn-primary btn-sm"><Mail size={14} /> Send invite</button>
            </div>
          </form>
        </div>
      )}
    </Panel>
  );
}

// What opening a plan shows: the budget maths, how much of it has gone, the Support at Home
// mechanics that hang off the total, this month's care management, and portal access. The care
// management form sits inline here rather than in a second overlay, so nothing stacks on the drawer.
// The plan as it was recorded from the Notice of Decision: whether it is published, when it starts,
// the period, the daily rate, and for a legacy Home Care Package its level and fees. All of it can
// be corrected; the budget is worked out again from what is saved.
const ymdOf = (v) => (v ? String(v instanceof Date ? v.toISOString() : v).slice(0, 10) : '');
function PlanDetails({ plan, onSaved }) {
  const sah = String(plan.scheme || '').toUpperCase() !== 'HCP';
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const start = () => setF({
    status: plan.status === 'published' ? 'published' : 'draft', startDate: ymdOf(plan.start_date), period: plan.period || (sah ? 'Quarterly' : 'Monthly'),
    dailySubsidyRate: String(Number(plan.daily_subsidy_rate || 0)), hcpLevel: String(plan.level || ''),
    careMgmtIsPct: plan.care_mgmt_is_pct !== false, careMgmtValue: String(Number(plan.care_mgmt_value || 0)),
    pkgMgmtIsPct: plan.pkg_mgmt_is_pct !== false, pkgMgmtValue: String(Number(plan.pkg_mgmt_value || 0)),
    incomeTestedFee: String(Number(plan.income_tested_fee || 0)), basicDailyFee: String(Number(plan.basic_daily_fee || 0)),
    transferredAmount: String(Number(plan.transferred_amount || 0)),
  });
  const set = (k) => (e) => { const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value; setF((p) => ({ ...p, [k]: v })); };
  const patch = async (body, msg) => {
    setSaving(true);
    try { await api.patch(`/aged-care/funding/${plan.id}`, body); toast.success(msg); setF(null); if (onSaved) onSaved(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the plan'); }
    finally { setSaving(false); }
  };
  const save = () => {
    const next = {
      ...plan, period: f.period, daily_subsidy_rate: Number(f.dailySubsidyRate) || 0,
      care_mgmt_is_pct: f.careMgmtIsPct, care_mgmt_value: Number(f.careMgmtValue) || 0, pkg_mgmt_is_pct: f.pkgMgmtIsPct, pkg_mgmt_value: Number(f.pkgMgmtValue) || 0,
      income_tested_fee: Number(f.incomeTestedFee) || 0, budget: null,
    };
    const body = {
      status: f.status, startDate: f.startDate || null, period: f.period, dailySubsidyRate: Number(f.dailySubsidyRate) || 0,
      transferredAmount: Number(f.transferredAmount) || 0, budget: Math.max(0, Math.round(computeBreakdown(next).totalBudget * 100) / 100),
    };
    if (!sah) Object.assign(body, {
      hcpLevel: Number(f.hcpLevel), careMgmtIsPct: f.careMgmtIsPct, careMgmtValue: Number(f.careMgmtValue) || 0, pkgMgmtIsPct: f.pkgMgmtIsPct,
      pkgMgmtValue: Number(f.pkgMgmtValue) || 0, incomeTestedFee: Number(f.incomeTestedFee) || 0, basicDailyFee: Number(f.basicDailyFee) || 0,
    });
    patch(body, 'Plan saved');
  };
  const draft = plan.status !== 'published';
  return (
    <Panel title="The plan" icon={FileText} bodyClass="px-4 py-3"
      actions={!f && <>
        {draft && <button type="button" disabled={saving} onClick={() => patch({ status: 'published' }, 'Plan published')} className="btn-primary btn-sm">Publish</button>}
        <button type="button" onClick={start} className="btn-secondary btn-sm"><Pencil size={13} /> Edit</button>
      </>}>
      {!f ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-[13px]">
          <Row label="Status" value={<PlanStatus status={plan.status} />} />
          <Row label="Starts" value={fmtDate(plan.start_date)} />
          <Row label={sah ? 'Classification' : 'Level'} value={plan.level ? String(plan.level) : 'Not set'} muted={!plan.level} />
          <Row label="Budget period" value={plan.period || 'Quarterly'} />
          <Row label="Daily subsidy" value={money(plan.daily_subsidy_rate)} />
          <Row label="Brought across from a previous provider" value={money(plan.transferred_amount)} muted={!Number(plan.transferred_amount)} />
          {draft && <p className="sm:col-span-2 text-[12px] text-amber-700 mt-1">This plan is a draft. Publish it once the Notice of Decision is checked.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          <FormRow cols={3}>
            <Field label="Status">{(id) => <select id={id} className={INP} value={f.status} onChange={set('status')}><option value="draft">Draft</option><option value="published">Published</option></select>}</Field>
            <Field label="Starts">{(id) => <input id={id} type="date" className={INP} value={f.startDate} onChange={set('startDate')} />}</Field>
            <Field label="Budget period">{(id) => <select id={id} className={INP} value={f.period} onChange={set('period')}><option>Quarterly</option><option>Monthly</option></select>}</Field>
          </FormRow>
          <FormRow cols={sah ? 2 : 3}>
            <Field label="Daily subsidy ($)" hint={sah ? 'The classification changes through Reclassify below.' : undefined}>{(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={f.dailySubsidyRate} onChange={set('dailySubsidyRate')} />}</Field>
            {!sah && <Field label="Level">{(id) => <select id={id} className={INP} value={f.hcpLevel} onChange={set('hcpLevel')}>{[1, 2, 3, 4].map((l) => <option key={l} value={l}>Level {l}</option>)}</select>}</Field>}
            <Field label="Brought across ($)" hint="Funds transferred from a previous provider.">{(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={f.transferredAmount} onChange={set('transferredAmount')} />}</Field>
          </FormRow>
          {!sah && (
            <FormRow cols={2}>
              <Field label={`Care management fee (${f.careMgmtIsPct ? '%' : '$'})`}>{(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={f.careMgmtValue} onChange={set('careMgmtValue')} />}</Field>
              <Field label={`Package management fee (${f.pkgMgmtIsPct ? '%' : '$'})`}>{(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={f.pkgMgmtValue} onChange={set('pkgMgmtValue')} />}</Field>
              <Field label="Basic daily fee ($)">{(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={f.basicDailyFee} onChange={set('basicDailyFee')} />}</Field>
              <Field label="Income-tested fee ($ a day)">{(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={f.incomeTestedFee} onChange={set('incomeTestedFee')} />}</Field>
            </FormRow>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setF(null)} className="btn-secondary btn-sm">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving' : 'Save'}</button>
          </div>
        </div>
      )}
    </Panel>
  );
}

// The participant's file shows the funding parts on its Funding tab and the people parts on its
// People tab; the funding register's drawer shows both.
export function FundingDetail({ plan, onSaved, sections = 'all' }) {
  return <BreakdownPanel plan={plan} onSaved={onSaved} sections={sections} />;
}

function BreakdownPanel({ plan, onLogCm, cm, onSaved, sections = 'all' }) {
  const showFunding = sections !== 'people';
  const showPeople = sections !== 'funding';
  const b = computeBreakdown(plan);
  const pct = b.totalBudget > 0 ? Math.round((b.utilised / b.totalBudget) * 100) : 0;
  const cmOpen = !!(cm && cm.plan && cm.plan.id === plan.id);
  return (
    <div className="space-y-4">
      {showFunding && <>
      {plan.id && <PlanDetails plan={plan} onSaved={onSaved} />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title={`${b.period} budget breakdown, ${b.days} days`} icon={Calculator} bodyClass="px-4 py-3">
          <div className="space-y-1.5 text-[13px]">
            <Row label={`Government subsidy at ${money(b.dailySubsidy)} a day`} value={money(b.subsidy)} />
            {b.supDetail.length > 0 ? (
              b.supDetail.map(s => <Row key={s.label} label={s.label} value={money(s.amount)} muted />)
            ) : (
              <Row label="Supplements" value={money(b.supplementsTotal)} muted />
            )}
            <Row label={b.sah ? 'To the care management account, 10%' : 'Less care management fee'} value={'− ' + money(b.careMgmt)} muted />
            {!b.sah && <Row label="Less package management fee" value={'− ' + money(b.pkgMgmt)} muted />}
            {!b.sah && <Row label="Less income-tested fee" value={'− ' + money(b.incomeTested)} muted />}
            <div className="flex justify-between pt-2 mt-1 border-t border-slate-200">
              <span className="font-semibold text-slate-800">Total budget</span>
              <span className="font-semibold text-slate-900 tabular-nums">{money(b.totalBudget)}</span>
            </div>
          </div>
        </Panel>

        <Panel title="Utilisation" icon={PiggyBank} bodyClass="px-4 py-3">
          <StatRow cols={3}>
            <MoneyStat label="Allocated" value={b.allocated} />
            <MoneyStat label="Utilised" value={b.utilised} />
            <MoneyStat label="Balance" value={b.balance} tone={b.balance < 0 ? 'rose' : 'slate'} />
          </StatRow>
          <div className="mt-4">
            <div className="flex justify-between text-[12px] text-slate-500 mb-1">
              <span>Spent</span>
              <span className="tabular-nums">{pct}% of budget</span>
            </div>
            <div className="h-1.5 rounded bg-slate-100 overflow-hidden">
              <div className={clsx('h-full', pct >= 100 ? 'bg-red-700' : pct > 85 ? 'bg-amber-500' : 'bg-primary-500')}
                style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
          </div>
        </Panel>
      </div>

      {/* SAH budget mechanics — 10% care-mgmt account / 90% service / interim / carryover */}
      <Panel title={b.sah ? 'How the Support at Home budget is used' : 'How the budget is used'} icon={PiggyBank} bodyClass="px-4 py-3">
        <StatRow cols={4}>
          <MoneyStat label="Care management account, 10%" value={b.totalBudget * 0.10} />
          <MoneyStat label="Service budget, 90%" value={b.totalBudget * 0.90} />
          <MoneyStat label={plan.is_interim ? 'Interim available, 60%' : 'Available in full'}
            value={plan.is_interim ? b.totalBudget * 0.60 : b.totalBudget} tone={plan.is_interim ? 'amber' : 'slate'} />
          <MoneyStat label="Carryover cap" value={Math.max(1000, b.totalBudget * 0.10)} />
        </StatRow>
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[13px] text-slate-600">
            <span>Care management this month</span>
            {plan.cm_compliant_this_month
              ? <AcStatus status="acknowledged" label="Logged" />
              : <AcStatus status="pending" label="Needed, at least one a month" />}
          </div>
          {onLogCm && plan.id && !cmOpen && (
            <button type="button" onClick={() => onLogCm(plan)} className="btn-secondary btn-sm">
              <Plus size={13} /> Log care management
            </button>
          )}
        </div>
        {cmOpen && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
            <FormRow cols={2}>
              <Field label="Date">
                {(id) => <input id={id} type="date" value={cm.form.date} onChange={e => cm.setForm(f => ({ ...f, date: e.target.value }))} className={INP} />}
              </Field>
              <Field label="Activity type">
                {(id) => (
                  <select id={id} value={cm.form.type} onChange={e => cm.setForm(f => ({ ...f, type: e.target.value }))} className={INP}>
                    <option>Care management contact</option>
                    <option>Care plan review</option>
                    <option>Budget review</option>
                    <option>Coordination / referral</option>
                    <option>Clinical oversight</option>
                  </select>
                )}
              </Field>
            </FormRow>
            <FormRow cols={2}>
              <Field label="Time (hours)" hint="Care management is claimed by the time spent.">
                {(id) => <input id={id} type="number" min="0" step="0.25" value={cm.form.hours} onChange={e => cm.setForm(f => ({ ...f, hours: e.target.value }))} className={INP} placeholder="0.5" />}
              </Field>
              <Field label="Hourly rate ($)" hint="Your published care management price.">
                {(id) => <input id={id} type="number" min="0" step="0.01" value={cm.form.rate} onChange={e => cm.setForm(f => ({ ...f, rate: e.target.value }))} className={INP} placeholder="0.00" />}
              </Field>
            </FormRow>
            <Field label="Notes">
              {(id) => <textarea id={id} rows={3} value={cm.form.notes} onChange={e => cm.setForm(f => ({ ...f, notes: e.target.value }))} className={INP} placeholder="What was done" />}
            </Field>
            {!(Number(cm.form.hours) > 0 && Number(cm.form.rate) > 0) && (
              <p className="text-[12px] text-amber-700">Without time and a rate this is logged as a contact only, and nothing is claimed for it.</p>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={cm.cancel} className="btn-secondary btn-sm">Cancel</button>
              <button type="button" onClick={cm.submit} disabled={cm.saving} className="btn-primary btn-sm">
                <Plus size={13} /> {cm.saving ? 'Saving' : 'Log activity'}
              </button>
            </div>
          </div>
        )}
      </Panel>

      </>}

      {showFunding && b.sah && <OnboardingPanel plan={plan} onSaved={onSaved} />}
      {showFunding && b.sah && <Reclassify plan={plan} onSaved={onSaved} />}
      {showFunding && b.sah && <AgreementVersions plan={plan} onSaved={onSaved} />}
      {showPeople && b.sah && <SelfManagement plan={plan} onSaved={onSaved} />}
      {showFunding && <ContributionProfile plan={plan} onSaved={onSaved} />}
      {showFunding && <SupplementsPanel plan={plan} onSaved={onSaved} />}
      {showFunding && b.sah && <UnspentHcpFunds plan={plan} onChanged={onSaved} />}
      {showFunding && <ExitWorkflow plan={plan} onSaved={onSaved} />}

      {showPeople && plan.client_id && <Supporters plan={plan} onSaved={onSaved} />}
      {showPeople && plan.client_id && <PortalAccess clientId={plan.client_id} clientName={plan.client_name || plan.clientName} />}
    </div>
  );
}

// The participant's own contribution rates, from the letter Services Australia sends after the
// means assessment. A part pensioner's rate is a taper on income and assets, so the means category
// alone only gives an estimate; the notice is what the participant must actually be charged.
function ContributionNoticeFields({ form, set }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2">
      <p className="text-[12px] text-slate-600">
        Contribution rates on the participant's Services Australia notice. Leave blank to use the standard rate for the means category.
        Clinical supports are always free.
      </p>
      <FormRow cols={3}>
        <Field label="Independence %">
          {(id) => <input id={id} type="number" min="0" max="100" step="0.01" className={INP} placeholder="e.g. 5"
            value={form.independencePct} onChange={e => set('independencePct', e.target.value)} />}
        </Field>
        <Field label="Everyday living %">
          {(id) => <input id={id} type="number" min="0" max="100" step="0.01" className={INP} placeholder="e.g. 17.5"
            value={form.everydayPct} onChange={e => set('everydayPct', e.target.value)} />}
        </Field>
        <Field label="Notice date">
          {(id) => <input id={id} type="date" className={INP}
            value={form.contributionNoticeDate || ''} onChange={e => set('contributionNoticeDate', e.target.value)} />}
        </Field>
      </FormRow>
    </div>
  );
}

const MEANS_LABEL = {
  full_pensioner: 'Full pensioner', part_pensioner: 'Part pensioner', self_funded: 'Self-funded',
  not_disclosed: 'Not assessed or not disclosed (maximum rates)',
};
const pctText = (v) => (v === null || v === undefined || v === '' ? null : `${Number(v)}%`);

function ContributionProfile({ plan, onSaved }) {
  const initial = useCallback(() => ({
    meansCategory: plan.means_category || 'full_pensioner',
    independencePct: plan.independence_pct ?? '',
    everydayPct: plan.everyday_pct ?? '',
    contributionNoticeDate: plan.contribution_notice_date ? String(plan.contribution_notice_date).slice(0, 10) : '',
    noWorseOff: !!plan.no_worse_off,
    protectedContributionPct: plan.protected_contribution_pct ?? '',
    pricesAgreedAt: plan.prices_agreed_at ? String(plan.prices_agreed_at).slice(0, 10) : '',
    overspendChargeAllowed: !!plan.overspend_charge_allowed,
  }), [plan]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial()); setEditing(false); }, [initial]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/aged-care/funding/${plan.id}`, {
        ...form,
        protectedContributionPct: form.noWorseOff ? form.protectedContributionPct : '',
        contributionNoticeDate: form.contributionNoticeDate || null,
        pricesAgreedAt: form.pricesAgreedAt || null,
      });
      toast.success('Contribution details saved. New services are charged at these rates.');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the contribution details');
    } finally { setSaving(false); }
  };
  if (!plan.id) return null;
  return (
    <Panel title="Contributions" icon={Calculator} bodyClass="px-4 py-3"
      actions={!editing && <button type="button" onClick={() => setEditing(true)} className="btn-secondary btn-sm">Edit</button>}>
      {!editing ? (
        <div className="space-y-1.5 text-[13px]">
          <Row label="Means category" value={MEANS_LABEL[plan.means_category] || 'Full pensioner (default)'} />
          <Row label="Independence rate" value={pctText(plan.independence_pct) ? `${pctText(plan.independence_pct)}, from the notice` : 'Standard rate for the means category'} />
          <Row label="Everyday living rate" value={pctText(plan.everyday_pct) ? `${pctText(plan.everyday_pct)}, from the notice` : 'Standard rate for the means category'} />
          {plan.contribution_notice_date && <Row label="Notice dated" value={fmtDate(plan.contribution_notice_date)} muted />}
          {plan.no_worse_off && <Row label="No worse off, protected rate" value={pctText(plan.protected_contribution_pct) || '—'} />}
          <Row label="Prices agreed with the participant" value={plan.prices_agreed_at ? fmtDate(plan.prices_agreed_at) : 'Not recorded'} muted={!plan.prices_agreed_at} />
          <Row label="What Services Australia does not pay (budget run out)" value={plan.overspend_charge_allowed ? 'The agreement lets you charge the participant' : 'Not charged to the participant'} muted={!plan.overspend_charge_allowed} />
        </div>
      ) : (
        <div className="space-y-3">
          <Field label="Means category">
            {(id) => (
              <select id={id} className={INP} value={form.meansCategory} onChange={e => set('meansCategory', e.target.value)}>
                {Object.entries(MEANS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}
          </Field>
          <ContributionNoticeFields form={form} set={set} />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.noWorseOff} onChange={e => set('noWorseOff', e.target.checked)}
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
            No worse off. A grandfathered Home Care Package transitionee.
          </label>
          {form.noWorseOff && (
            <Field label="Protected contribution %" className="max-w-xs">
              {(id) => <input id={id} type="number" min="0" max="100" step="0.01" className={INP}
                value={form.protectedContributionPct} onChange={e => set('protectedContributionPct', e.target.value)} />}
            </Field>
          )}
          <Field label="Prices agreed with the participant on" hint="The date they agreed the prices in their service agreement.">
            {(id) => <input id={id} type="date" className={clsx(INP, 'max-w-xs')} value={form.pricesAgreedAt} onChange={e => set('pricesAgreedAt', e.target.value)} />}
          </Field>
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.overspendChargeAllowed} onChange={e => set('overspendChargeAllowed', e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
            <span>The service agreement lets us charge the participant for a service Services Australia does not pay because their budget ran out. Leave this off unless the agreement says so.</span>
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setForm(initial()); setEditing(false); }} className="btn-secondary btn-sm">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving' : 'Save'}</button>
          </div>
        </div>
      )}
    </Panel>
  );
}

// Leaving Support at Home (or this provider). The final claim is due 60 days after the exit date,
// and the dashboard counts down to it.
// The dates the Support at Home workflow runs on (program manual v4.4). Each one has a rule:
//   referral accepted within 3 days of arriving in the My Aged Care portal;
//   service agreement signed before the first service (14-day cooling-off, reviewed every 12 months);
//   entry registered in the Aged Care Provider Portal within 56 days (84 with an extension), or
//   Services Australia pays nothing for the services.
// A pending hardship application means no contribution may be demanded until it is decided.
const ONBOARDING = [
  ['funding_allocated_at', 'fundingAllocatedAt', 'Funding allocated', 'The date on the Notice of Decision. Services must start within 56 days.'],
  ['referral_received_at', 'referralReceivedAt', 'Referral received', 'In the My Aged Care provider portal.'],
  ['referral_accepted_at', 'referralAcceptedAt', 'Referral accepted', 'Within 3 days of receiving it.'],
  ['agreement_signed_at', 'agreementSignedAt', 'Service agreement signed', 'Before the first service. Reviewed every 12 months.'],
  ['entry_registered_at', 'entryRegisteredAt', 'Entry registered in the Provider Portal', 'Within 56 days. No payment until it is.'],
  ['services_in_place_at', 'servicesInPlaceAt', 'Services in place recorded in My Aged Care', 'After the first service, on their client record in the My Aged Care provider portal.'],
];
function OnboardingPanel({ plan, onSaved }) {
  const d = (v) => (v ? String(v).slice(0, 10) : '');
  const [f, setF] = useState(() => Object.fromEntries(ONBOARDING.map(([col]) => [col, d(plan[col])])));
  const [hardship, setHardship] = useState(plan.hardship_status || (plan.hardship_pending ? 'applied' : ''));
  const [hsApplied, setHsApplied] = useState(d(plan.hardship_applied_at));
  const [hsDecided, setHsDecided] = useState(d(plan.hardship_decided_at));
  const [extended, setExtended] = useState(!!plan.take_up_extended);
  const [interim, setInterim] = useState(!!plan.is_interim);
  const [approved, setApproved] = useState(plan.approved_service_types || []);
  const [naps, setNaps] = useState(plan.naps_service_id || '');
  const [types, setTypes] = useState([]);   // the service types the Notice of Decision can approve
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/aged-care/sah-reference').then((r) => {
      const all = (r.data?.services || []).filter((x) => !/^SERV-00(6[1-8])$/.test(x.id) && !/care management/i.test(x.type));
      setTypes([...new Set(all.map((x) => x.type))]);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    setF(Object.fromEntries(ONBOARDING.map(([col]) => [col, d(plan[col])])));
    setHardship(plan.hardship_status || (plan.hardship_pending ? 'applied' : ''));
    setHsApplied(d(plan.hardship_applied_at));
    setHsDecided(d(plan.hardship_decided_at));
    setExtended(!!plan.take_up_extended);
    setInterim(!!plan.is_interim);
    setApproved(plan.approved_service_types || []);
    setNaps(plan.naps_service_id || '');
  }, [plan]);
  if (!plan.id) return null;
  const sameList = (a, b) => [...(a || [])].sort().join('|') === [...(b || [])].sort().join('|');
  const dirty = ONBOARDING.some(([col]) => f[col] !== d(plan[col])) || hardship !== (plan.hardship_status || (plan.hardship_pending ? 'applied' : ''))
    || hsApplied !== d(plan.hardship_applied_at) || hsDecided !== d(plan.hardship_decided_at)
    || extended !== !!plan.take_up_extended || interim !== !!plan.is_interim || !sameList(approved, plan.approved_service_types)
    || naps.trim() !== (plan.naps_service_id || '');
  const toggleType = (t) => setApproved((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const startBy = f.funding_allocated_at ? (() => { const x = new Date(`${f.funding_allocated_at}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + (extended ? 84 : 56)); return x.toISOString().slice(0, 10); })() : null;
  const downloadAgreement = async () => {
    try {
      const r = await api.get(`/aged-care/funding/${plan.id}/agreement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `service-agreement-${String(plan.client_name || 'participant').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not build the service agreement'); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { hardshipStatus: hardship || null, hardshipAppliedAt: hsApplied || null, hardshipDecidedAt: hsDecided || null, takeUpExtended: extended, isInterim: interim, approvedServiceTypes: approved, napsServiceId: naps.trim() || null };
      for (const [col, key] of ONBOARDING) body[key] = f[col] || null;
      await api.patch(`/aged-care/funding/${plan.id}`, body);
      toast.success('Onboarding saved');
      if (onSaved) onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  };
  const review = f.agreement_signed_at ? (() => { const x = new Date(`${f.agreement_signed_at}T00:00:00Z`); x.setUTCFullYear(x.getUTCFullYear() + 1); return x.toISOString().slice(0, 10); })() : null;
  return (
    <Panel title="Onboarding" icon={ClipboardCheck} bodyClass="px-4 py-3"
      actions={(
        <span className="flex items-center gap-2">
          <button type="button" onClick={downloadAgreement} className="btn-secondary btn-sm"><FileDown size={13} /> Service agreement</button>
          {dirty && <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving…' : 'Save'}</button>}
        </span>
      )}>
      <FormRow cols={2}>
        {ONBOARDING.map(([col, , label, hint]) => (
          <Field key={col} label={label} hint={hint}>
            {(id) => <input id={id} type="date" className={INP} value={f[col]} onChange={(e) => setF((p) => ({ ...p, [col]: e.target.value }))} />}
          </Field>
        ))}
        <Field label="NAPS service ID" hint="Only if this participant is claimed under a different service from the one in Claiming settings.">
          {(id) => <input id={id} className={INP} value={naps} maxLength={40} onChange={(e) => setNaps(e.target.value)} placeholder="Same as the organisation" />}
        </Field>
      </FormRow>
      {review && <p className="text-[12px] text-slate-500 mt-2">Agreement review due {fmtDate(review)}. Cooling-off ends {fmtDate((() => { const x = new Date(`${f.agreement_signed_at}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + 14); return x.toISOString().slice(0, 10); })())}.</p>}
      {startBy && !f.entry_registered_at && (
        <p className="text-[12px] text-amber-800 mt-1">Services must start, with entry registered, by {fmtDate(startBy)} or the funding is withdrawn.</p>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-[13px] text-slate-700">
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={extended} onChange={(e) => setExtended(e.target.checked)} /> 28-day extension granted (84 days to start)</label>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={interim} onChange={(e) => setInterim(e.target.checked)} /> Interim funding (60% now, the rest added later)</label>
      </div>
      {types.length > 0 && (
        <div className="mt-3">
          <p className="text-[13px] font-medium text-slate-800">Approved in the Notice of Decision</p>
          <p className="text-[12px] text-slate-500 mb-1.5">Only services of these types can be claimed. Leave all off if it is not recorded yet.</p>
          <div className="flex flex-wrap gap-1.5">
            {types.map((t) => (
              <button key={t} type="button" onClick={() => toggleType(t)} aria-pressed={approved.includes(t)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${approved.includes(t) ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>{t}</button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3">
        <p className="text-[13px] font-medium text-slate-800">Hardship (decided by Services Australia)</p>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <select aria-label="Hardship" className={`${INP} !w-auto`} value={hardship} onChange={(e) => setHardship(e.target.value)}>
            <option value="">No application</option>
            <option value="applied">Applied, being assessed</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
          </select>
          {hardship && <label className="flex items-center gap-1.5 text-[12px] text-slate-600">Applied <input type="date" className={`${INP} !w-auto`} value={hsApplied} onChange={(e) => setHsApplied(e.target.value)} /></label>}
          {(hardship === 'approved' || hardship === 'declined') && <label className="flex items-center gap-1.5 text-[12px] text-slate-600">Decided <input type="date" className={`${INP} !w-auto`} value={hsDecided} onChange={(e) => setHsDecided(e.target.value)} /></label>}
        </div>
        <p className="text-[12px] text-slate-500 mt-1">
          {hardship === 'applied' ? 'While it is assessed, no contribution invoice is made and unpaid contribution reminders stop.'
            : hardship === 'approved' ? 'The reduced contribution comes back on each payment statement, and invoices follow it.'
              : hardship === 'declined' ? 'Contribution invoices go out as usual, including any held back while it was assessed.'
                : 'Record an application here so no contribution is invoiced while Services Australia assesses it.'}
        </p>
      </div>
    </Panel>
  );
}


// Unspent Home Care Package funds a transitioned participant brought across. Claims use the
// provider-held portion first, then the government-held portion; provider-held money goes back to
// government when the participant exits or changes provider.
// A classification change from Services Australia (reassessment, or interim to full): the new
// classification from its effective date, this quarter's budget pro rata, and a review reminder.
function Reclassify({ plan, onSaved }) {
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState('');
  const [eff, setEff] = useState(isoDate());
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (plan.id) api.get(`/aged-care/funding/${plan.id}/versions`).then((r) => setVersions(r.data || [])).catch(() => {}); }, [plan.id]);
  if (!plan.id) return null;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/aged-care/funding/${plan.id}/reclassify`, { level: Number(level), effectiveDate: eff });
      toast.success(r.data.budgetAfter != null ? `Now classification ${r.data.level}. This quarter's budget ${money(r.data.budgetBefore)} becomes ${money(r.data.budgetAfter)}.` : `Now classification ${r.data.level}.`);
      setOpen(false); setLevel(''); if (onSaved) onSaved();
      api.get(`/aged-care/funding/${plan.id}/versions`).then((x) => setVersions(x.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); } finally { setBusy(false); }
  };
  return (
    <Panel title="Classification" icon={ClipboardCheck} bodyClass="px-4 py-3 space-y-2 text-[13px]"
      actions={!open && <button type="button" className="btn-secondary btn-sm" onClick={() => setOpen(true)}>Classification changed</button>}>
      <p className="text-slate-700">Classification {plan.level ?? 'not recorded'}{plan.reclassified_at ? ` since ${fmtDate(plan.reclassified_at)}` : ''}.</p>
      {open && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="New classification">{(id) => <select id={id} className={`${INP} !w-auto`} value={level} onChange={(e) => setLevel(e.target.value)}><option value="">Choose…</option>{[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>Classification {n}</option>)}</select>}</Field>
          <Field label="Takes effect">{(id) => <input id={id} type="date" className={`${INP} !w-auto`} value={eff} onChange={(e) => setEff(e.target.value)} />}</Field>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          <button type="button" className="btn-primary btn-sm" disabled={busy || !level} onClick={save}>Record</button>
        </div>
      )}
      {versions.length > 0 && (
        <ul className="text-[12px] text-slate-600 space-y-0.5">
          {versions.map((v) => <li key={v.id}>{fmtDate(v.effective_from)}: classification {v.level_from ?? '?'} to {v.level_to}{v.budget_after != null ? `, quarter budget ${money(v.budget_before)} to ${money(v.budget_after)}` : ''}</li>)}
        </ul>
      )}
    </Panel>
  );
}

// The plan's supplements: each at the government rate on the day, or the provider's own figure;
// and whether the participant brings the care management supplement into the care management account.
function SupplementsPanel({ plan, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/aged-care/supplement-rates').then((r) => setRates(r.data)).catch(() => setRates(null)); }, []);
  if (!plan.id) return null;
  const sah = String(plan.scheme || '').toUpperCase() !== 'HCP';
  const start = () => {
    const cur = plan.supplements && typeof plan.supplements === 'object' ? plan.supplements : {};
    setForm(Object.fromEntries(Object.entries(cur).map(([k, v]) => [k, v === 'ref' || v === true ? { mode: 'ref', amt: '' } : { mode: 'own', amt: String(v) }])));
    setReason(plan.cm_supplement_reason || '');
    setEditing(true);
  };
  const setMode = (key, mode) => setForm((f) => { const n = { ...f }; if (mode === 'none') delete n[key]; else n[key] = { mode, amt: (f[key] && f[key].amt) || '' }; return n; });
  const save = async () => {
    setSaving(true);
    try {
      const supplements = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.mode === 'ref' ? 'ref' : Number(v.amt) || 0]).filter(([, v]) => v === 'ref' || v > 0));
      await api.patch(`/aged-care/funding/${plan.id}`, { supplements, ...(sah ? { cmSupplementReason: reason || null } : {}) });
      toast.success('Supplements saved'); setEditing(false); if (onSaved) onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the supplements'); }
    finally { setSaving(false); }
  };
  const lines = Array.isArray(plan.supplement_lines) ? plan.supplement_lines : [];
  const rateText = (key) => {
    const r = rates && rates.rates && rates.rates[key];
    if (!r) return null;
    return r.pct ? `${r.value}% of the classification's daily funding` : `${money(r.value)} a day from ${fmtDate(r.from)}`;
  };
  return (
    <Panel title="Supplements" icon={Calculator} bodyClass="px-4 py-3"
      actions={!editing && <button type="button" onClick={start} className="btn-secondary btn-sm">Edit</button>}>
      {!editing ? (
        <div className="space-y-1.5 text-[13px]">
          {lines.length === 0 && <p className="text-slate-500">None recorded.</p>}
          {lines.map((l) => <Row key={l.key} label={SUPP_LABEL[l.key] || l.key} value={`${money(l.daily)} a day${l.from === 'government' ? ', government rate' : ', own figure'}`} />)}
          {sah && <Row label="Care management supplement" value={plan.cm_supplement_reason ? (rates?.reasons?.[plan.cm_supplement_reason] || plan.cm_supplement_reason) : 'Not in one of its groups'} muted={!plan.cm_supplement_reason} />}
        </div>
      ) : (
        <div className="space-y-2">
          {SUPPLEMENTS.map(({ key, label, gov }) => {
            const cur = form[key];
            const mode = cur ? cur.mode : 'none';
            return (
              <div key={key} className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="flex-1 min-w-[180px] text-slate-700">{label}{gov && rateText(key) ? <span className="block text-[11.5px] text-slate-500">{rateText(key)}</span> : null}</span>
                <select aria-label={`${label}: how it is paid`} className="input-field !w-auto !py-1 !text-[12px]" value={mode} onChange={(e) => setMode(key, e.target.value)}>
                  <option value="none">Not paid</option>
                  {gov && <option value="ref">Government rate</option>}
                  <option value="own">Own figure</option>
                </select>
                {mode === 'own' && (
                  <input type="number" min="0" step="0.01" aria-label={`${label}: dollars a day`} className="input-field !w-28 !py-1 !text-[12px]" value={cur.amt}
                    onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, [key]: { mode: 'own', amt: v } })); }} />
                )}
              </div>
            );
          })}
          {sah && (
            <Field label="Care management supplement" hint={rates?.careManagement ? `${money(rates.careManagement.value)} a day into your care management account while the participant is in one of these groups.` : undefined}>
              {(id) => (
                <select id={id} className={INP} value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="">Not in one of its groups</option>
                  {Object.entries(rates?.reasons || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              )}
            </Field>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} className="btn-secondary btn-sm">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving' : 'Save'}</button>
          </div>
        </div>
      )}
    </Panel>
  );
}

const HCP_SOURCE = { AT: 'Assistive technology', AS: 'Assistive technology', HM: 'Home modifications', EL: 'End-of-Life', ON: 'Services above the quarterly budget' };
function UnspentHcpFunds({ plan, onChanged }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);           // 'use' | 'set' | null
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [bal, setBal] = useState({ provider: '', government: '', participant: '' });
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const load = useCallback(async () => {
    if (!plan.id) return;
    try { const r = await api.get(`/aged-care/funding/${plan.id}/hcp-unspent`); setData(r.data); }
    catch { setData({ provider: 0, government: 0, ledger: [] }); }
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);
  if (!plan.id || !data) return null;
  const total = Number(data.provider || 0) + Number(data.government || 0);
  const done = async (msg) => { toast.success(msg); setMode(null); setAmount(''); setNote(''); await load(); if (onChanged) onChanged(); };
  const use = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/aged-care/funding/${plan.id}/hcp-unspent/use`, { amount: Number(amount), note });
      await done(`Used ${money(r.data.fromProvider)} held by you and ${money(r.data.fromGovernment)} held by Services Australia`);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); }
    finally { setBusy(false); }
  };
  const setBalances = async () => {
    setBusy(true);
    try {
      await api.put(`/aged-care/funding/${plan.id}/hcp-unspent`, { provider: Number(bal.provider) || 0, government: Number(bal.government) || 0, participant: Number(bal.participant) || 0, note });
      await done('Balances saved');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the balances'); }
    finally { setBusy(false); }
  };
  // The Commonwealth portion goes back to Services Australia; the participant portion is refunded
  // to the participant or estate, or on a change of provider transferred to the new provider.
  const giveBack = async (reason) => {
    const who = reason === 'provider_change' ? 'transferred to the new provider (within 60 days)' : `refunded to the ${reason === 'death' ? 'estate' : 'participant'}`;
    const ok = await confirm(`${money(data.commonwealth)} (Commonwealth portion) is returned to Services Australia, and ${money(data.participant)} (participant portion) is ${who}.`,
      { title: reason === 'provider_change' ? 'Participant changed provider?' : reason === 'death' ? 'Participant passed away?' : 'Participant exited?', confirmLabel: 'Record it' });
    if (!ok) return;
    try { await api.post(`/aged-care/funding/${plan.id}/hcp-unspent/return`, { reason }); await done('Recorded'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); }
  };
  return (
    <Panel title="Unspent Home Care Package funds" icon={PiggyBank} bodyClass="px-4 py-3"
      actions={!mode && <button type="button" onClick={() => { setBal({ provider: data.provider, government: data.government, participant: data.participant || 0 }); setMode('set'); }} className="btn-secondary btn-sm">Set balances</button>}>
      <StatRow cols={3}>
        <MoneyStat label="Held by you, used first" value={Number(data.provider || 0)} />
        <MoneyStat label="Held by Services Australia" value={Number(data.government || 0)} />
        <MoneyStat label="Total left" value={total} />
      </StatRow>
      {Number(data.provider) > 0 && (
        <p className="mt-2 text-[12px] text-slate-600">Of what you hold: participant portion {money(data.participant)}, Commonwealth portion {money(data.commonwealth)}.</p>
      )}
      {data.expected && (data.expected.drawn.provider > 0 || data.expected.drawn.government > 0) && (
        <div className="mt-2 text-[12px] text-slate-600 space-y-1">
          <p>
            Claims so far are expected to have drawn {money(data.expected.drawn.provider)} of the Commonwealth portion you hold and {money(data.expected.drawn.government)} of Services Australia's,
            leaving {money(data.expected.provider)} and {money(data.expected.government)}. Services Australia draws these funds itself: for assistive technology and home modifications before
            their AT-HM funding, for End-of-Life services after that budget, and for other services once the quarterly budget is used up. Tell the portal when a claim draws on what you hold.
          </p>
          <ul className="space-y-0.5">
            {data.expected.lines.slice(0, 8).map((l) => (
              <li key={l.key}>{fmtDate(l.date)} · {HCP_SOURCE[l.source] || l.source} · {money(l.hcpProvider + l.hcpGovernment)}</li>
            ))}
          </ul>
        </div>
      )}
      {total === 0 && !mode && (
        <p className="mt-2 text-[12px] text-slate-500">None recorded. Only participants who moved over from a Home Care Package have these.</p>
      )}
      {!mode && total > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => setMode('use')} className="btn-secondary btn-sm">Record funds used</button>
          {Number(data.provider) > 0 && <>
            <button type="button" onClick={() => giveBack('exit')} className="btn-secondary btn-sm">Participant exited</button>
            <button type="button" onClick={() => giveBack('death')} className="btn-secondary btn-sm">Participant passed away</button>
            <button type="button" onClick={() => giveBack('provider_change')} className="btn-secondary btn-sm">Changed provider</button>
          </>}
        </div>
      )}
      {mode === 'use' && (
        <div className="mt-3 space-y-2">
          <FormRow cols={2}>
            <Field label="Amount used ($)" hint="From the Commonwealth portion you hold first. Only for something not claimed here: claimed visits and items draw on these funds by themselves.">
              {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={amount} onChange={e => setAmount(e.target.value)} />}
            </Field>
            <Field label="What it paid for">
              {(id) => <input id={id} className={INP} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. services above the quarterly budget" />}
            </Field>
          </FormRow>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setMode(null)} className="btn-secondary btn-sm">Cancel</button>
            <button type="button" onClick={use} disabled={busy || !(Number(amount) > 0)} className="btn-primary btn-sm">Record</button>
          </div>
        </div>
      )}
      {mode === 'set' && (
        <div className="mt-3 space-y-2">
          <FormRow cols={2}>
            <Field label="Held by you ($)">
              {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={bal.provider} onChange={e => setBal(b => ({ ...b, provider: e.target.value }))} />}
            </Field>
            <Field label="Held by Services Australia ($)">
              {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={bal.government} onChange={e => setBal(b => ({ ...b, government: e.target.value }))} />}
            </Field>
          </FormRow>
          <Field label="Participant portion of what you hold ($)" hint="From their HCP statement: the part that came from their own fees. It is refunded to them (or moved to a new provider), never returned to government.">
            {(id) => <input id={id} type="number" min="0" step="0.01" className={INP} value={bal.participant} onChange={e => setBal(b => ({ ...b, participant: e.target.value }))} />}
          </Field>
          <Field label="Source">
            {(id) => <input id={id} className={INP} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Services Australia statement dated 1 Nov 2025" />}
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setMode(null)} className="btn-secondary btn-sm">Cancel</button>
            <button type="button" onClick={setBalances} disabled={busy} className="btn-primary btn-sm">Save</button>
          </div>
        </div>
      )}
      {data.ledger?.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="text-[12px] font-medium text-slate-600 mb-1">History</p>
          <ul className="space-y-1 text-[12px] text-slate-600">
            {data.ledger.slice(0, 12).map(l => (
              <li key={l.id} className="flex justify-between gap-3">
                <span>{fmtDate(l.entry_date)} · {({ opening: 'Brought across', use: 'Used', return: 'Returned to government', adjust: 'Balance set', refund: 'Refunded', transfer: 'Transferred to the new provider' })[l.kind] || l.kind}, {l.source === 'provider' ? 'held by you' : 'held by Services Australia'}{l.note ? ` — ${l.note}` : ''}</span>
                <span className="tabular-nums">{money(l.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

// Create a Support at Home participant.
//
// `clients.service_type` is the single switch deciding which module a person belongs to, and until
// now nothing in the UI could set it to 'aged_care' — the general client form does not offer it,
// and only Plan Management and Support Coordination stamped their own value on create. So aged care
// participants could only be seeded or inserted by hand.
//
// Email is asked for here, and flagged when missing, because it is not optional in practice: a
// monthly statement is a legal obligation and cannot be issued without an address on file.
// Exported so the Participants tab uses the same form rather than a second copy of it — one
// definition, so the two screens cannot drift on which fields a participant needs.
export function NewParticipantModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    first_name: '', last_name: '', aged_care_recipient_id: '', sa_care_recipient_id: '', date_of_birth: '',
    gender: '', email: '', mobile: '', address: '', suburb: '', state: '', postcode: '',
  });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const canSave = f.first_name.trim() && f.last_name.trim() && !saving;

  const save = async (allowDuplicate = false) => {
    if (!canSave) return;
    setSaving(true);
    try {
      const r = await api.post('/aged-care/clients', { ...f, allow_duplicate: allowDuplicate });
      toast.success(`${r.data.name} added`);
      if (r.data.warning) toast(r.data.warning, { icon: <AlertTriangle size={16} className="text-amber-600 shrink-0" aria-hidden="true" />, duration: 7000 });
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d?.duplicate) {
        // Never silently create the second record — the same person may legitimately exist once per
        // module, so the operator decides.
        const ok = await confirm(d.message, { title: 'Create a new aged care participant anyway?', confirmLabel: 'Create participant anyway' });
        if (ok) {
          setSaving(false);
          return save(true);
        }
      } else {
        toast.error(d?.message || 'Could not create the participant');
      }
    } finally { setSaving(false); }
  };

  return (
    <CreateModal
      title="New Support at Home participant"
      onClose={onClose}
      size="md"
      footer={<>
        <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button type="button" onClick={() => save(false)} disabled={!canSave} className="btn-primary text-sm">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
          Create participant
        </button>
      </>}
    >
      <p className="text-[12px] text-slate-500">They will belong to aged care only, and will not appear in the general clients list.</p>

      <FormRow cols={2}>
        <Field label="First name" required>{(id) => <input id={id} className={INP} value={f.first_name} onChange={(e) => set('first_name', e.target.value)} />}</Field>
        <Field label="Last name" required>{(id) => <input id={id} className={INP} value={f.last_name} onChange={(e) => set('last_name', e.target.value)} />}</Field>
      </FormRow>

      <FormRow cols={2}>
        <Field label="My Aged Care ID">{(id) => <input id={id} className={INP} value={f.aged_care_recipient_id} onChange={(e) => set('aged_care_recipient_id', e.target.value)} placeholder="AC12345678" />}</Field>
        <Field label="Services Australia care recipient ID" hint="9 digits, from the Provider Portal entry. Needed on every claim.">{(id) => <input id={id} inputMode="numeric" className={INP} value={f.sa_care_recipient_id} onChange={(e) => set('sa_care_recipient_id', e.target.value.replace(/[^0-9]/g, ''))} placeholder="404006845" />}</Field>
        <Field label="Date of birth">{(id) => <input id={id} type="date" className={INP} value={f.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />}</Field>
      </FormRow>

      <FormRow cols={2}>
        <Field label="Email">
          {(id) => (
            <>
              <input id={id} type="email" className={INP} value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="For their monthly statement" />
              {!f.email.trim() && (
                <p className="text-[11.5px] text-amber-700 mt-1">Without an email their monthly statement cannot be sent, and a statement is required every month.</p>
              )}
            </>
          )}
        </Field>
        <Field label="Mobile">{(id) => <input id={id} className={INP} value={f.mobile} onChange={(e) => set('mobile', e.target.value)} />}</Field>
      </FormRow>

      <Field label="Address">{(id) => <input id={id} className={INP} value={f.address} onChange={(e) => set('address', e.target.value)} />}</Field>

      <FormRow cols={3}>
        <Field label="Suburb">{(id) => <input id={id} className={INP} value={f.suburb} onChange={(e) => set('suburb', e.target.value)} />}</Field>
        <Field label="State">
          {(id) => (
            <select id={id} className={INP} value={f.state} onChange={(e) => set('state', e.target.value)}>
              <option value="">—</option>
              {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          )}
        </Field>
        <Field label="Postcode">{(id) => <input id={id} className={INP} value={f.postcode} onChange={(e) => set('postcode', e.target.value)} />}</Field>
      </FormRow>
    </CreateModal>
  );
}

export default function AgedCareFunding() {
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await api.get('/aged-care/funding');
      setPlans(Array.isArray(r.data) ? r.data : (r.data?.plans || []));
    } catch (e) {
      setPlans([]);
      setLoadError(e.response?.data?.message || 'Could not load the funding plans');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const r = await api.get('/aged-care/clients');
      setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || []));
    } catch {
      setClients([]);
    }
  };

  useEffect(() => {
    load();
    loadClients();
  }, []);

  // Care-management activity logging (min 1 per participant per month)
  const [cmPlan, setCmPlan] = useState(null);
  const [cmForm, setCmForm] = useState({ date: isoDate(), type: 'Care management contact', notes: '', hours: '', rate: '' });
  const [cmSaving, setCmSaving] = useState(false);
  const logCm = async () => {
    if (!cmPlan) return;
    setCmSaving(true);
    try {
      const hours = Number(cmForm.hours) || 0; const rate = Number(cmForm.rate) || 0;
      await api.post('/aged-care/care-management', {
        fundingId: cmPlan.id, activityDate: cmForm.date, activityType: cmForm.type, notes: cmForm.notes,
        units: hours > 0 ? hours : undefined, rate: rate > 0 ? rate : undefined,
      });
      toast.success(hours > 0 ? 'Care management logged. Approve it under Care management to claim it.' : 'Care management contact logged');
      setCmPlan(null);
      setCmForm({ date: isoDate(), type: 'Care management contact', notes: '', hours: '', rate: '' });
      load();
    } catch { toast.error('Could not log the activity'); }
    finally { setCmSaving(false); }
  };

  const totals = plans.reduce((acc, p) => {
    const b = computeBreakdown(p);
    acc.budget += b.totalBudget;
    acc.spent += b.utilised;
    acc.balance += b.balance;
    return acc;
  }, { budget: 0, spent: 0, balance: 0 });

  // One breakdown per row, computed once, so every money column reads the same numbers.
  const rows = useMemo(() => plans.map((p) => ({
    ...p, _key: p.id ?? `${p.client_id}-${p.scheme}`, _b: computeBreakdown(p),
  })), [plans]);
  const open = expanded == null ? null : rows.find((r) => r._key === expanded) || null;
  // Stable, or the drawer re-runs its focus effect on every keystroke inside it.
  // The drawer closes on Escape and on a click outside it, and the care-management form now lives
  // INSIDE it — so closing would silently discard whatever had been typed. Ask first, and only when
  // there is actually something to lose.
  // The unsaved note is read through a ref: depending on the note text would give the drawer a new
  // onClose on every keystroke in it, and the drawer would pull focus out of the note each time.
  const confirm = useConfirm();
  const cmDraft = useRef({ plan: null, notes: '' });
  cmDraft.current = { plan: cmPlan, notes: cmForm.notes };
  const closeDrawer = useCallback(async () => {
    const { plan, notes } = cmDraft.current;
    if (plan && String(notes || '').trim()) {
      const ok = await confirm('It has not been saved.', { title: 'Discard this care management note?', confirmLabel: 'Discard note', danger: true });
      if (!ok) return;
    }
    setExpanded(null); setCmPlan(null);
  }, [confirm]);
  const cancelCm = useCallback(() => setCmPlan(null), []);

  const columns = [
    {
      key: 'client', header: 'Participant', sortable: true,
      sortValue: (p) => p.client_name || p.clientName || '',
      render: (p) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-slate-900">{p.client_name || p.clientName || '—'}</span>
            {p.no_worse_off && <span className="badge badge-purple" title="Grandfathered Home Care Package, no worse off">No worse off</span>}
          </div>
          {(p.start_date || p.startDate) && (
            <div className="text-[11px] text-slate-500">From {fmtDate(p.start_date || p.startDate)}</div>
          )}
        </div>
      ),
    },
    { key: 'scheme', header: 'Scheme', sortable: true, width: 90, render: (p) => <span title={schemeName(p.scheme)}>{String(p.scheme || '—').toUpperCase()}</span> },
    { key: 'level', header: 'Classification', render: (p) => levelLabel(p) },
    { key: 'period', header: 'Period', width: 100, render: (p) => p._b.period },
    { key: 'budget', header: 'Budget', align: 'right', sortable: true, sortValue: (p) => p._b.totalBudget, render: (p) => <Currency value={p._b.totalBudget} className="font-medium" /> },
    { key: 'spent', header: 'Spent', align: 'right', sortable: true, sortValue: (p) => p._b.utilised, render: (p) => <Currency value={p._b.utilised} /> },
    { key: 'balance', header: 'Balance', align: 'right', sortable: true, sortValue: (p) => p._b.balance, render: (p) => <Currency value={p._b.balance} className="font-medium text-slate-900" /> },
    { key: 'status', header: 'Status', width: 110, render: (p) => <PlanStatus status={p.status} /> },
  ];

  return (
    <div>
      <PageHead icon={Wallet}
        title="Funding plans"
        purpose="Every participant's Support at Home or Home Care Package budget: what the government pays, what is deducted, and what is left to spend."
        actions={<>
          {/* A participant has to exist before they can be funded, and aged care had no way to
              make one: service_type is the switch that decides the module, and nothing in the UI
              could set it. */}
          <button type="button" onClick={() => setShowNewClient(true)} className="btn-secondary text-sm">
            <UserPlus size={16} /> New participant
          </button>
          <button type="button" onClick={() => setShowModal(true)} className="btn-primary text-sm">
            <Plus size={16} /> New funding plan
          </button>
        </>}
      />

      <FigureStrip>
        <Figure label="Active plans" value={plans.filter((p) => !p.exited_at).length} sub={plans.some((p) => p.exited_at) ? `${plans.filter((p) => p.exited_at).length} for people who have left` : null} />
        <Figure label="Total budget" value={money(totals.budget)} />
        <Figure label="Spent" value={money(totals.spent)} />
        <Figure label="Balance" value={money(totals.balance)} tone={totals.balance < 0 ? 'red' : 'slate'} />
      </FigureStrip>

      <Register
        title="Funding plans" count={plans.length} countLabel={plans.length === 1 ? 'plan' : 'plans'}
        columns={columns} rows={rows} rowKey="_key" loading={loading} error={loadError} onRetry={load}
        onRowClick={(p) => setExpanded(p._key)} activeRowKey={expanded}
        empty={{
          icon: Wallet,
          title: 'No funding plans yet',
          hint: 'Create a Support at Home or Home Care Package plan to track a participant’s budget, supplements and fees.',
          action: <button type="button" onClick={() => setShowModal(true)} className="btn-primary btn-sm"><Plus size={14} /> New funding plan</button>,
        }}
      />

      <DetailDrawer
        open={!!open} onClose={closeDrawer} icon={Wallet} width="xl"
        title={open ? (open.client_name || open.clientName || 'Funding plan') : ''}
        statusNode={open ? <PlanStatus status={open.status} /> : null}
        subtitle={open ? `${schemeName(open.scheme)}, ${levelLabel(open).toLowerCase()}, ${open._b.period.toLowerCase()} budget` : undefined}
        meta={open ? [
          { label: 'Starts', value: fmtDate(open.start_date || open.startDate) },
          { label: 'Budget', value: money(open._b.totalBudget), money: true },
          { label: 'Balance', value: money(open._b.balance), money: true },
          open.no_worse_off && { label: 'Transition', value: 'No worse off' },
        ] : undefined}
      >
        {open && (
          <BreakdownPanel
            plan={open} onLogCm={setCmPlan} onSaved={load}
            cm={{ plan: cmPlan, form: cmForm, setForm: setCmForm, saving: cmSaving, submit: logCm, cancel: cancelCm }}
          />
        )}
      </DetailDrawer>

      {showNewClient && (
        <NewParticipantModal
          onClose={() => setShowNewClient(false)}
          onCreated={() => loadClients()}
        />
      )}

      {showModal && (
        <NewFundingModal
          clients={clients}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
